import React from 'react';

export const TimelineComparison: React.FC = () => {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Traditional Sale vs. Selling to Us
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Understanding the difference in timelines and processes can help you make an informed decision about how to sell your apartment building.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-gray-50 rounded-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gray-400 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Traditional Sale</h3>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">1</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Property Preparation</div>
                  <div className="text-gray-600 text-sm">2-4 months: Make repairs, handle deferred maintenance, improve curb appeal</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">2</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">List with Agent</div>
                  <div className="text-gray-600 text-sm">1-2 weeks: Interview agents, sign listing agreement, prepare marketing materials</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">3</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Marketing Period</div>
                  <div className="text-gray-600 text-sm">3-6 months: Property showings, open houses, waiting for qualified buyers</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">4</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Negotiations</div>
                  <div className="text-gray-600 text-sm">2-4 weeks: Offers, counteroffers, inspection negotiations, repair requests</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">5</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Due Diligence</div>
                  <div className="text-gray-600 text-sm">4-8 weeks: Buyer inspections, financing approval, appraisal, title work</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-bold text-gray-700">6</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Closing</div>
                  <div className="text-gray-600 text-sm">1-2 weeks: Final paperwork, closing coordination</div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-300">
                <div className="text-2xl font-bold text-gray-900">Total: 6-12+ Months</div>
                <div className="text-gray-600 mt-2">Plus 5-7% in commissions and fees</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 border-2 border-teal-500">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-flashlight-line text-xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Selling to Us</h3>
            </div>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">1</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Initial Contact</div>
                  <div className="text-gray-700 text-sm">Day 1: Reach out to us with basic property information</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">2</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Property Evaluation</div>
                  <div className="text-gray-700 text-sm">Days 2-5: We review financials, inspect property, analyze market data</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">3</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Written Offer</div>
                  <div className="text-gray-700 text-sm">Days 5-7: Receive our formal written offer with clear terms</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">4</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Agreement</div>
                  <div className="text-gray-700 text-sm">Days 7-10: Review and sign purchase agreement</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">5</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Due Diligence</div>
                  <div className="text-gray-700 text-sm">Days 10-21: Our team completes inspections and title work</div>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-bold text-white">6</div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1">Closing</div>
                  <div className="text-gray-700 text-sm">Days 21-30: Close on your timeline, receive your funds</div>
                </div>
              </div>
              <div className="pt-4 border-t border-teal-300">
                <div className="text-2xl font-bold text-teal-600">Total: 30-45 Days</div>
                <div className="text-gray-700 mt-2">No commissions, no fees, no repairs needed</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            The Value of Speed and Certainty
          </h3>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-600 leading-relaxed">
            <p>
              The timeline difference between a traditional sale and selling to us is substantial. While a traditional sale might take six months to a year with no guarantee of success, we can close in as little as 30 days with certainty. This speed provides several important benefits beyond just getting your money faster.
            </p>
            <p>
              First, a shorter timeline means lower carrying costs. Every month you own the property, you pay for insurance, property taxes, utilities, maintenance, and potentially mortgage payments. Over six months, these costs can add up to tens of thousands of dollars. By closing quickly, you eliminate these ongoing expenses.
            </p>
            <p>
              Second, speed reduces risk. Real estate markets can change quickly. A property that is worth a certain amount today might be worth less in six months if market conditions shift. By closing quickly, you lock in your sale price and eliminate market risk.
            </p>
            <p>
              Third, certainty allows you to plan your future. When you list with an agent, you have no idea when or if your property will sell. This uncertainty makes it difficult to make other life decisions. When you work with us, you know exactly when you will close and how much you will receive, allowing you to plan your next steps with confidence.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
